import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Card, CardContent, Avatar, useMediaQuery, Badge, Switch, FormControlLabel, Chip, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { useTheme } from '@mui/material/styles';
import './Notifications.css';

import CloseIcon from '@mui/icons-material/Close';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ArchiveIcon from '@mui/icons-material/Archive';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import axios from '../axios';
import Layout from './Layout';
import moment from 'moment';
import 'moment/locale/it';

const Notifications = () => {
    const [notifications, setNotifications] = useState([]);
    const theme = useTheme();
    const [showDeleted, setShowDeleted] = useState(false);
    const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));

    const fetchNotifications = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post('/datahandler/notifications', {
                token,
                showDeleted
            });
            setNotifications(response.data);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        }
    };

    useEffect(() => {
        fetchNotifications();
    }, [showDeleted]);

    const handleRemoveNotification = async (id) => {
        try {
            const token = localStorage.getItem('token');
            await axios.post(`/datahandler/notifications/viewed`, {
                id,
                token
            });
            setNotifications(notifications.filter(notification => notification.id !== id));
        } catch (error) {
            console.error('Error updating notification status:', error);
        }
    };

    const handleToggleShowDeleted = () => {
        setShowDeleted((prev) => !prev);
    };

    // Strip external mt.php links and flag for button rendering
    const processMessage = (message) => {
        if (!message) return { text: message, hasCorsiLink: false };
        const linkRegex = /(<a[^>]*href=["'])?https?:\/\/www\.hocus-lotus\.edu\/mt\.php\?t=[a-zA-Z0-9]+(["'][^>]*>.*?<\/a>)?/gi;
        const hasCorsiLink = linkRegex.test(message);
        // Remove the URL (and any <a> tag wrapping it) plus trailing whitespace
        const text = message.replace(linkRegex, '').replace(/clicca qui:\s*/gi, '').trim();
        return { text, hasCorsiLink };
    };

    const renderNotificationCard = (notification, index) => {
        const isArchived = notification.status === "archived";
        const { text: processedMessage, hasCorsiLink } = processMessage(notification.message);

        return (
            <Card
                key={notification.id}
                className={`notification-card notification-animate ${isArchived ? 'notification-card-archived' : ''}`}
                elevation={0}
                sx={{
                    display: 'flex',
                    alignItems: isSmallScreen ? 'flex-start' : 'center',
                    flexDirection: isSmallScreen ? 'column' : 'row',
                    backgroundColor: isArchived
                        ? 'rgba(224, 224, 224, 0.45)'
                        : 'rgba(208, 228, 254, 0.45)',
                    color: '#000000',
                    marginBottom: 2,
                    animationDelay: `${index * 0.06}s`,
                    opacity: 0, // Will be animated in
                }}
            >
                {/* Left side: Avatar */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: isSmallScreen ? 1.5 : 2,
                }}>
                    <Avatar
                        sx={{
                            width: 48,
                            height: 48,
                            background: isArchived
                                ? 'linear-gradient(135deg, #9e9e9e, #757575)'
                                : 'linear-gradient(135deg, #ffa726, #e65100)',
                            boxShadow: isArchived
                                ? '0 3px 10px rgba(0,0,0,0.1)'
                                : '0 3px 10px rgba(230,81,0,0.3)',
                        }}
                    >
                        {isArchived ? <ArchiveIcon /> : <NotificationsActiveIcon />}
                    </Avatar>
                </Box>

                {/* Center: Content */}
                <CardContent sx={{
                    flex: 1,
                    padding: isSmallScreen ? '8px 12px' : '12px 16px',
                    '&:last-child': { pb: isSmallScreen ? '12px' : '16px' },
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#333' }}>
                            {isArchived ? 'Notifica archiviata' : 'Promemoria per te'}
                        </Typography>
                        <Chip
                            icon={<CalendarTodayIcon sx={{ fontSize: '14px !important' }} />}
                            label={moment(notification.created_at).format('DD/MM/YYYY')}
                            size="small"
                            sx={{
                                height: 24,
                                fontSize: '0.72rem',
                                fontWeight: 600,
                                bgcolor: isArchived ? 'rgba(0,0,0,0.08)' : 'rgba(25,118,210,0.12)',
                                color: isArchived ? '#666' : '#1565c0',
                                '& .MuiChip-icon': { color: 'inherit' },
                            }}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography
                            variant="body2"
                            component="span"
                            sx={{ color: '#444', lineHeight: 1.6 }}
                            dangerouslySetInnerHTML={{ __html: processedMessage }}
                        />
                        {hasCorsiLink && (
                            <Button
                                component={Link}
                                to="/corsi-insegnante"
                                variant="contained"
                                className="btn-3d btn-3d-dark-orange"
                                startIcon={<MenuBookIcon />}
                                size="small"
                                sx={{ display: 'inline-flex', textTransform: 'none', fontWeight: 700, py: 0.4, px: 1.5, fontSize: '0.8rem' }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                I miei corsi
                            </Button>
                        )}
                    </Box>
                </CardContent>

                {/* Right side: Close button */}
                {notification.status !== "archived" && (
                    <Box sx={{ p: 1 }}>
                        <IconButton
                            onClick={() => handleRemoveNotification(notification.id)}
                            size="small"
                            sx={{
                                color: '#999',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    color: '#e53935',
                                    bgcolor: 'rgba(229,57,53,0.08)',
                                    transform: 'scale(1.1)',
                                },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                )}
            </Card>
        );
    };

    return (
        <Layout>
            <Box className="dashboard-header">
                <Typography className="dashboard-title">
                    Notifiche
                </Typography>
                <Typography variant="body1" className="dashboard-subtitle">
                    Le tue notifiche e promemoria.
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={showDeleted}
                            onChange={handleToggleShowDeleted}
                            color="primary"
                        />
                    }
                    label={
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {showDeleted ? "Mostra notifiche eliminate" : "Notifiche attive"}
                        </Typography>
                    }
                    labelPlacement="start"
                />
            </Box>

            {notifications.length === 0 ? (
                <Box
                    className="notification-card notification-animate"
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 6,
                        px: 3,
                        borderRadius: '16px',
                        backgroundColor: 'rgba(255,255,255,0.45)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.5)',
                    }}
                >
                    <Avatar
                        sx={{
                            width: 64,
                            height: 64,
                            mb: 2,
                            background: 'linear-gradient(135deg, #e0e0e0, #bdbdbd)',
                        }}
                    >
                        <NotificationsIcon sx={{ fontSize: 32 }} />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#666' }}>
                        Nessuna notifica
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#999', mt: 0.5 }}>
                        Non ci sono notifiche da mostrare al momento.
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ width: '100%', maxWidth: isSmallScreen ? 400 : '100%', mx: 'auto' }}>
                    {notifications.map((notification, index) =>
                        renderNotificationCard(notification, index)
                    )}
                </Box>
            )}
        </Layout>
    );
};

export default Notifications;
